import React, { useMemo, useState } from 'react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Tabs } from '../../components/ui/Tabs';
import { Modal } from '../../components/ui/Modal';
import { DataTable } from '../../components/ui/DataTable';
import { EmptyState } from '../../components/ui/EmptyState';
import { PermissionGate } from '../../components/admin/PermissionGate';
import { FeatureGate } from '../../components/platform/FeatureStatusBadge';
import { IconTile, PowerStatusPill } from '../../components/power/PowerPrimitives';
import { usePower } from '../../contexts/PowerContext';
import { useGovernance } from '../../contexts/GovernanceContext';
import { useSectionRoute } from '../../hooks/useSectionRoute';
import { EVIDENCE_LABEL, EVIDENCE_WEIGHT, EVIDENCE_WINDOW_MINUTES, FRESHNESS_THRESHOLDS, POWER_SOURCE_LABEL, POWER_STATE_LABEL, RELIABILITY_MINIMUM, REPORT_LIMITS, powerLocationById, powerLocations } from '../../data/power';
import { CONFIDENCE_LABEL, confirmedLabel, freshnessLabel } from '../../utils/power';
import type { PowerState } from '../../types/power';
const TABS = ['Overview', 'Locations', 'Reports', 'Anomalies', 'Configuration'] as const;

/**
 * Campuzon Power — administration.
 *
 * Every figure here is derived from the same reports the student surfaces read,
 * so the console cannot disagree with the product. Moderation is reversible and
 * never destructive: a dismissed report stops counting but keeps its record.
 */
export function PowerControl() {
  const [tab, setTab] = useSectionRoute('/admin/power', TABS);
  const {
    allReports,
    reports,
    moderationLog,
    getLocationStatus,
    getReliability,
    dismissReport,
    restoreReport
  } = usePower();
  const {
    logAction,
    me
  } = useGovernance();
  const [moderating, setModerating] = useState<{
    id: string;
    restore: boolean;
  } | null>(null);

  // Statuses for every mapped location, derived on read.
  const statuses = useMemo(() => powerLocations.map(location => ({
    location,
    status: getLocationStatus(location.id)
  })), [getLocationStatus]);
  const byState = useMemo(() => {
    const counts: Record<PowerState, number> = {
      on: 0,
      off: 0,
      unstable: 0,
      backup: 0,
      unknown: 0
    };
    for (const entry of statuses) counts[entry.status.state] += 1;
    return counts;
  }, [statuses]);

  /**
   * Anomalies are observations for a human, never automatic action: a location
   * where recent evidence genuinely disagrees, and single reports that contradict
   * the aggregate they sit in.
   */
  const anomalies = useMemo(() => {
    const conflicted = statuses.filter(entry => entry.status.conflicting);
    const outliers = reports.filter(report => {
      const status = getLocationStatus(report.locationId);
      return status.state !== 'unknown' && report.state !== status.state && status.reportCount >= 3;
    });
    return {
      conflicted,
      outliers
    };
  }, [getLocationStatus, reports, statuses]);
  return <div className="space-y-6" data-id="element-7481" data-mp="1e4z1el3xmmmv">
      <PageHeader eyebrow="Campuzon Power" title="Power control centre" description="Monitored locations, the evidence behind each status, moderation and the thresholds the aggregation uses." data-id="element-7482" data-mp="1e61osj21q6y1" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Power sections" data-id="element-7483" data-mp="1s3qcdynhzs6s" />

      {tab === 'Overview' && <div className="space-y-6" data-id="element-7484" data-mp="1x29vzsemcrvz">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5" data-id="element-7485" data-mp="1x18kxnxls5e9">
            <SummaryTile label="Monitored locations" value={powerLocations.length} note="Location entities, never coordinates belonging to a person." data-id="element-7486" data-mp="21t9siwjtt7xy" />
            {(['on', 'off', 'unstable', 'backup'] as PowerState[]).map(state => <SummaryTile key={state} label={POWER_STATE_LABEL[state]} value={byState[state]} note={`Derived from reports inside the ${EVIDENCE_WINDOW_MINUTES}-minute window.`} data-id="element-7487" data-mp="2fsrobvvjhlgt" />)}
          </div>

          <Panel title="Coverage" description="Where the platform genuinely has nothing to say." bodyClassName="p-4" data-id="element-7488" data-mp="2bw42r28x0y5p">
            {byState.unknown === 0 ? <p className="text-body-sm text-ink" data-id="element-7489" data-mp="1j76iwup22tau">
                Every monitored location has recent evidence.
              </p> : <>
                <p className="text-body-sm text-ink" data-id="element-7490" data-mp="1eazn3uqdiy6m">
                  <span className="cz-num font-semibold" data-id="element-7491" data-mp="1t6xc8t1vr29c">{byState.unknown}</span> of{' '}
                  <span className="cz-num font-semibold" data-id="element-7492" data-mp="1o6jeq4xwnapk">{powerLocations.length}</span>{' '}
                  locations have no reports inside the evidence window. Those render as
                  &ldquo;no recent data&rdquo; to students rather than as powered.
                </p>
                <ul className="mt-2 flex flex-wrap gap-2" data-id="element-7493" data-mp="374xvhfhhlw4k">
                  {statuses.filter(entry => entry.status.state === 'unknown').map(entry => <li key={entry.location.id} data-id="element-7494" data-mp="2y5aqyktgoa7a">
                        <Badge plain data-id="element-7495" data-mp="2y6luqkmwphye">{entry.location.name}</Badge>
                      </li>)}
                </ul>
              </>}
          </Panel>

          <Panel title="Reliability readiness" description={`A percentage is withheld until ${RELIABILITY_MINIMUM.reports} reports across ${RELIABILITY_MINIMUM.days} days exist.`} bodyClassName="p-4" data-id="element-7496" data-mp="3px9ubs8xmf9y">
            <ul className="space-y-2" data-id="element-7497" data-mp="3l58mciisrapq">
              {statuses.map(entry => {
            const reliability = getReliability(entry.location.id);
            return <li key={entry.location.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-line pb-2 last:border-0 last:pb-0" data-id="element-7498" data-mp="cnc9l1wpz9vi">
                    <span className="text-body-sm text-ink" data-id="element-7499" data-mp="nak6qj6ud60y">{entry.location.name}</span>
                    <span className="cz-num text-caption text-ink-muted" data-id="element-7500" data-mp="n8ppbeus3w1m">
                      {reliability.sufficient ? `${Math.round((reliability.uptimeShare ?? 0) * 100)}% powered · ${reliability.sampleDays} days` : `${reliability.reportCount}/${RELIABILITY_MINIMUM.reports} reports — not enough yet`}
                    </span>
                  </li>;
          })}
            </ul>
          </Panel>

          <FeatureGate featureKey="power_meter" data-id="element-7501" data-mp="3vqvgrmwgahm8" />
        </div>}

      {tab === 'Locations' && <Panel title="Monitored locations" bodyClassName="p-0" data-id="element-7502" data-mp="3qt3t552u9hnu">
          <DataTable caption="Power locations and their derived status" rows={statuses} rowKey={entry => entry.location.id} columns={[{
        header: 'Location',
        primary: true,
        cell: entry => <span data-id="element-7504" data-mp="3bmy0ormhnetu">
                    {entry.location.name}
                    {entry.location.personal && <span className="ml-2 text-caption text-ink-subtle" data-id="element-7505" data-mp="3go3uksrvx84y">private</span>}
                  </span>
      }, {
        header: 'Kind',
        cell: entry => entry.location.kind
      }, {
        header: 'Status',
        status: true,
        cell: entry => <PowerStatusPill state={entry.status.state} size="xs" data-id="element-7506" data-mp="2xwuhk9iwo18m" />
      }, {
        header: 'Confidence',
        cell: entry => CONFIDENCE_LABEL[entry.status.confidence]
      }, {
        header: 'Reports',
        align: 'right',
        cell: entry => entry.status.reportCount
      }, {
        header: 'Last confirmed',
        cell: entry => confirmedLabel(entry.status.lastConfirmedAt)
      }]} data-id="element-7503" data-mp="3lsfupyca5yjl" />
        </Panel>}

      {tab === 'Reports' && <PermissionGate permission="moderation.resolve" label="Moderating power reports" data-id="element-7507" data-mp="26s45fk33mm0">
          <div className="space-y-6" data-id="element-7508" data-mp="3tc9zme1vg3ws">
            <Panel title="Report queue" description="Setting a report aside stops it counting towards the current answer. It is never deleted, and it can be restored." bodyClassName="p-0" data-id="element-7509" data-mp="3tb8hjpmfuh5k">
              <ul className="divide-y divide-line" data-id="element-7510" data-mp="kwqbtujyzdp4">
                {allReports.slice(0, 40).map(report => <li key={report.id} className="flex flex-wrap items-center gap-3 p-4" data-id="element-7511" data-mp="fxewmpz94g9n">
                    <div className="min-w-0 flex-1" data-id="element-7512" data-mp="uuo0gj5qrci9">
                      <div className="flex flex-wrap items-center gap-2" data-id="element-7513" data-mp="uvfjkui5jhrn">
                        <span className="text-body-sm font-medium text-ink" data-id="element-7514" data-mp="pxe22e0djqia">
                          {powerLocationById(report.locationId)?.name ?? report.locationId}
                        </span>
                        <PowerStatusPill state={report.state} size="xs" data-id="element-7515" data-mp="1di6mpb1p8yfy" />
                        {report.dismissed && <Badge tone="neutral" data-id="element-7516" data-mp="zinhaq09w17c">Set aside</Badge>}
                      </div>
                      <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-7517" data-mp="14hqks60ltzd7">
                        {EVIDENCE_LABEL[report.evidence]} ·{' '}
                        {POWER_SOURCE_LABEL[report.source]} ·{' '}
                        {freshnessLabel(report.reportedAt)}
                      </p>
                      {report.dismissed && <p className="mt-1 text-caption text-ink-muted" data-id="element-7518" data-mp="gr4ccdmoud2o">
                          Set aside by {report.dismissed.by}: {report.dismissed.reason}
                        </p>}
                    </div>
                    <button type="button" onClick={() => setModerating({
                id: report.id,
                restore: Boolean(report.dismissed)
              })} className="min-h-touch rounded-xl border border-line bg-canvas px-3.5 text-body-sm font-medium text-ink hover:border-line-strong" data-id="element-7519" data-mp="btunp074i5fw">
                      {report.dismissed ? 'Restore' : 'Set aside'}
                    </button>
                  </li>)}
              </ul>
            </Panel>

            <Panel title="Moderation history" description="Appended, with the reason and the administrator who decided." bodyClassName="p-4" data-id="element-7520" data-mp="3dky58i3u83z5">
              {moderationLog.length === 0 ? <EmptyState title="No moderation decisions yet" description="Every decision recorded here keeps its reason and its author." data-id="element-7521" data-mp="38rd5olmlj1xu" /> : <ul className="space-y-2" data-id="element-7522" data-mp="543axr13vrwo">
                  {moderationLog.map(entry => <li key={entry.id} className="border-b border-line pb-2 last:border-0" data-id="element-7523" data-mp="1psf9c10u647z">
                      <p className="text-body-sm text-ink" data-id="element-7524" data-mp="1kv5j4j5mhoj3">
                        {entry.action === 'dismissed' ? 'Set aside' : 'Restored'} —{' '}
                        {powerLocationById(entry.locationId)?.name ?? entry.locationId}
                      </p>
                      <p className="text-caption text-ink-subtle" data-id="element-7525" data-mp="16ul3fvv088tt">
                        {entry.by} · {freshnessLabel(entry.at)} · {entry.reason}
                      </p>
                    </li>)}
                </ul>}
            </Panel>
          </div>
        </PermissionGate>}

      {tab === 'Anomalies' && <div className="space-y-6" data-id="element-7526" data-mp="1bsweub0y6re5">
          <Panel title="Locations where evidence disagrees" description="An observation for a person to act on, not a finding. Nothing is removed automatically." bodyClassName="p-4" data-id="element-7527" data-mp="1bw20thea5d5n">
            {anomalies.conflicted.length === 0 ? <EmptyState title="No conflicting locations" description="Recent reports agree everywhere the platform has evidence." data-id="element-7528" data-mp="1jxoxsiampw5s" /> : <ul className="space-y-3" data-id="element-7529" data-mp="1twwgzm41ol0t">
                {anomalies.conflicted.map(entry => <li key={entry.location.id} className="flex flex-wrap items-center gap-3" data-id="element-7530" data-mp="1xwx0hl603meq">
                    <PowerStatusPill state={entry.status.state} size="xs" data-id="element-7531" data-mp="119abu9f658bu" />
                    <span className="min-w-0 flex-1 text-body-sm text-ink" data-id="element-7532" data-mp="167btqr0uz1lq">
                      {entry.location.name}
                    </span>
                    <span className="cz-num text-caption text-ink-muted" data-id="element-7533" data-mp="1a6ayaaiogwcn">
                      {Math.round(entry.status.agreement * 100)}% agreement ·{' '}
                      {entry.status.reportCount} reports
                    </span>
                  </li>)}
              </ul>}
          </Panel>

          <Panel title="Reports contradicting their location" description="A single report differing from the aggregate is usually a genuine change in progress, not manipulation. Read before acting." bodyClassName="p-4" data-id="element-7534" data-mp="ezjxwnyqe3lo">
            {anomalies.outliers.length === 0 ? <EmptyState title="No contradicting reports" description="Nothing in the current window differs from the status it contributes to." data-id="element-7535" data-mp="yhcmdfk8xbt" /> : <ul className="space-y-2" data-id="element-7536" data-mp="3ndajc4d1yvr5">
                {anomalies.outliers.slice(0, 12).map(report => <li key={report.id} className="flex flex-wrap items-center gap-2" data-id="element-7537" data-mp="3ig0izto4kzoj">
                    <PowerStatusPill state={report.state} size="xs" data-id="element-7538" data-mp="3efqeklfzehbs" />
                    <span className="min-w-0 flex-1 text-body-sm text-ink" data-id="element-7539" data-mp="39fc4yyyqezbn">
                      {powerLocationById(report.locationId)?.name}
                    </span>
                    <span className="text-caption text-ink-subtle" data-id="element-7540" data-mp="18curv1whli0m">
                      {EVIDENCE_LABEL[report.evidence]} · {freshnessLabel(report.reportedAt)}
                    </span>
                  </li>)}
              </ul>}
          </Panel>
        </div>}

      {tab === 'Configuration' && <div className="space-y-6" data-id="element-7541" data-mp="13l304o1lh68w">
          <Panel title="Aggregation configuration" description="These values decide every status the platform shows. They are configuration, not numbers written into components." bodyClassName="p-4" data-id="element-7542" data-mp="13k9zm4t8bcz7">
            <dl className="grid gap-4 sm:grid-cols-2" data-id="element-7543" data-mp="fopb0q8k0d62">
              <ConfigRow label="Evidence window" value={`${EVIDENCE_WINDOW_MINUTES} minutes`} note="Reports older than this contribute nothing to the current answer." data-id="element-7544" data-mp="fqa4sdn1ns2v" />
              <ConfigRow label="Freshness thresholds" value={FRESHNESS_THRESHOLDS.map(item => `${item.level} ≤ ${item.withinMinutes}m`).join(' · ')} note="Anything beyond the last threshold reads as stale." data-id="element-7545" data-mp="2f11f18jgemi1" />
              <ConfigRow label="Report limits" value={`${REPORT_LIMITS.perHour} per hour · ${REPORT_LIMITS.perLocationCooldownMinutes}m per location`} note="Enforced on submission, so one account cannot move a status alone." data-id="element-7546" data-mp="d49a2b0h9fab" />
              <ConfigRow label="Reliability minimum" value={`${RELIABILITY_MINIMUM.reports} reports across ${RELIABILITY_MINIMUM.days} days`} note="Below this, no percentage is published at all." data-id="element-7547" data-mp="h5l36qik8in9" />
            </dl>
          </Panel>

          <Panel title="Evidence weighting" description="What each kind of evidence counts for. No source is weighted so heavily that it cannot be contradicted." bodyClassName="p-4" data-id="element-7548" data-mp="2xtoyx0g4ytxb">
            <ul className="space-y-2" data-id="element-7549" data-mp="3ik2mx51jkq7f">
              {Object.entries(EVIDENCE_WEIGHT).map(([source, weight]) => <li key={source} className="flex items-center justify-between gap-3 border-b border-line pb-2 last:border-0 last:pb-0" data-id="element-7550" data-mp="3ml4ohsixleck">
                  <span className="text-body-sm text-ink" data-id="element-7551" data-mp="e7grtz7lo9c">
                    {EVIDENCE_LABEL[source as keyof typeof EVIDENCE_LABEL]}
                  </span>
                  <span className="cz-num text-caption text-ink-muted" data-id="element-7552" data-mp="9dun8u6spk99">×{weight}</span>
                </li>)}
            </ul>
          </Panel>

          <PermissionGate permission="config.edit" label="Changing power thresholds" data-id="element-7553" data-mp="e5kz54ldsg2s">
            <Panel title="Editing these values" bodyClassName="p-4" data-id="element-7554" data-mp="j3e4jvev1620">
              <p className="max-w-[70ch] text-body-sm text-ink" data-id="element-7555" data-mp="o3s38mukn3nd">
                Thresholds and weights are published through platform configuration rather
                than edited here, so a change carries a reason, a previous value and an audit
                entry like every other configuration change.
              </p>
            </Panel>
          </PermissionGate>

          <FeatureGate featureKey="power_notifications" data-id="element-7556" data-mp="23lybr61uat6r" />
        </div>}

      {moderating && <ModerationDialog restore={moderating.restore} onClose={() => setModerating(null)} onConfirm={reason => {
      const by = me?.name ?? 'Administrator';
      const report = allReports.find(item => item.id === moderating.id);
      if (moderating.restore) restoreReport(moderating.id, reason, by);else dismissReport(moderating.id, reason, by);
      logAction({
        action: moderating.restore ? 'power.report.restored' : 'power.report.dismissed',
        resource: 'Power report',
        resourceId: moderating.id,
        before: report?.dismissed ? 'set aside' : 'counting',
        after: moderating.restore ? 'counting' : 'set aside',
        reason
      });
      setModerating(null);
    }} data-id="element-7557" data-mp="1yn3krgtpxybk" />}
    </div>;
}
function SummaryTile({
  label,
  value,
  note
}: {
  label: string;
  value: number;
  note: string;
}) {
  return <div className="rounded-card border border-line bg-surface p-4 shadow-panel" data-id="element-7558" data-mp="2ekp5n9eihbp3">
      <div className="flex items-center gap-2" data-id="element-7559" data-mp="32b2n65zgzs5f">
        <IconTile name="power" tone="brand" size="sm" data-id="element-7560" data-mp="32bb0u03f31ho" />
        <p className="text-caption font-medium uppercase tracking-normal text-ink-subtle" data-id="element-7561" data-mp="36c4ocscy6ea1">
          {label}
        </p>
      </div>
      <p className="cz-num mt-2 text-h2 font-semibold text-ink" data-id="element-7562" data-mp="2sbtwpn3tcvnt">{value}</p>
      <p className="mt-1 text-caption text-ink-subtle" data-id="element-7563" data-mp="2xb6sixcnxd2j">{note}</p>
    </div>;
}
function ConfigRow({
  label,
  value,
  note
}: {
  label: string;
  value: string;
  note: string;
}) {
  return <div data-id="element-7564" data-mp="1lx2qd3vx8ime">
      <dt className="text-caption font-medium uppercase tracking-normal text-ink-subtle" data-id="element-7565" data-mp="1lwt9q6wrnntd">
        {label}
      </dt>
      <dd className="cz-num mt-1 text-body-sm font-medium text-ink" data-id="element-7566" data-mp="1ebpe8w195qyn">{value}</dd>
      <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-7567" data-mp="19eqkieli3m0z">{note}</p>
    </div>;
}
function ModerationDialog({
  restore,
  onClose,
  onConfirm
}: {
  restore: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const enough = reason.trim().length >= 12;
  return <Modal open onClose={onClose} title={restore ? 'Restore this report?' : 'Set this report aside?'} description={restore ? 'It will count towards the location status again.' : 'It stops counting towards the location status. The record, its reason and your name are kept.'} data-id="element-7568" data-mp="upkrx9h64u22">
      <div className="space-y-4" data-id="element-7569" data-mp="bya08q3mcd94">
        <label className="block" data-id="element-7570" data-mp="bxi7dn1eotmj">
          <span className="text-body-sm font-medium text-ink" data-id="element-7571" data-mp="bwgqv31mfexf">Why?</span>
          <textarea value={reason} onChange={event => setReason(event.target.value)} rows={3} className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-body text-ink focus:border-brand-700 focus:outline-none" data-id="element-7572" data-mp="6yoyk7x2dqsp" />
          <span className="mt-1 block text-caption text-ink-subtle" data-id="element-7573" data-mp="39b9mvteea3t3">
            A reason of substance is required. It is stored with the decision.
          </span>
        </label>
        <div className="flex gap-2.5" data-id="element-7574" data-mp="3iael0ym1nv4q">
          <button type="button" onClick={onClose} className="min-h-touch flex-1 rounded-xl border border-line bg-canvas px-4 text-body-sm font-medium text-ink hover:border-line-strong" data-id="element-7575" data-mp="3i9cx3tjxje5l">
            Cancel
          </button>
          <button type="button" disabled={!enough} onClick={() => onConfirm(reason.trim())} className="min-h-touch flex-1 rounded-xl bg-brand-700 px-4 text-body-sm font-semibold text-white hover:bg-brand-800 disabled:cursor-not-allowed disabled:bg-canvas disabled:text-ink-muted" data-id="element-7576" data-mp="3nbaoxc6ebha5">
            {restore ? 'Restore' : 'Set aside'}
          </button>
        </div>
      </div>
    </Modal>;
}